import memoize from "memoizee"
import { Dispatch } from "redux"
import {
  ref,
  get,
  query,
  orderByChild,
  startAt,
  endAt,
} from "firebase/database"

import dayjs from "dayjs"
import gql from "graphql-tag"
import { LessonType, LessonTypeName } from "@coast/utils"
import {
  SLOTS_GET_SUCCESS,
  SCHEDULE_FETCH_ERROR,
  OUT_OF_SERVICE_AREA,
  RESET_SEARCH,
  STOP_SEARCH,
  IS_SEARCHING,
  IS_DONE_SEARCHING,
  SET_SEARCH_DATES,
  SET_MEETING_POINTS,
  SET_SPOT_TAKEN,
  SET_SELECTED_LESSON_TYPE,
  SET_INCLUDE_PRIVATE,
} from "./types"
import {
  preferAfternoons,
  schedulingApiKey,
  DAYS_AT_A_TIME,
  DEFAULT_TIMEZONE,
  STATE_CONFIG_OVERRIDES,
} from "../config"
import apiConfig from "../apiConfig"
import { hoursToMinutes } from "../utils/misc"
import { logError, logErrorMessage } from "../utils/errors"
import { makeApiCall } from "../utils/gatewayUtils"
import {
  AddressPoint,
  Coordinates,
  Instructor,
  LessonDateTime,
  SchedulerReturn,
  SchedulerSlot,
  SlotsData,
  SlotToPick,
  StateConfig,
} from "../types"
import type { AppThunkAction } from "../store/configureStore"
import { sdk9db } from "../firebase"
import clientNext from "../ApolloClientNext"
import { GetMeetingPointsDocument } from "../components/template-parts/availability-picker/SearchLogicWrapper.graphqlTypes"
import { lessonTypes } from "../content"
import { GQLAddressPointFragment } from "./searchActions.graphqlTypes"

gql`
  fragment AddressPoint on StateMeetingPoint {
    id
    address
    label
    distanceMiles
  }
`
const DATE_FORMAT = "YYYY-MM-DD"
const MAX_MEMOIZE_MS = 5 * 60 * 1000
const MIN_RESULT_DAYS = 7
const MAX_DAYS_OUT = 60

export type StopSearchAction = ReturnType<typeof stopSearch>
export const stopSearch = () => ({ type: STOP_SEARCH, payload: true })

export type ResetSearchAction = ReturnType<typeof resetSearch>
export const resetSearch = () => ({ type: RESET_SEARCH })

export type SetSelectedLessonTypeAction = ReturnType<
  typeof setSelectedLessonType
>
export const setSelectedLessonType = (selectedLessonType: LessonType) => ({
  type: SET_SELECTED_LESSON_TYPE,
  payload: selectedLessonType,
})

export type SetDefaultLessonTypeForStateAction = ReturnType<
  typeof setDefaultLessonTypeForState
>
export const setDefaultLessonTypeForState =
  (stateShortName: string) => (dispatch: Dispatch) => {
    switch (stateShortName) {
      case "IL":
        dispatch(setSelectedLessonType(lessonTypes.oneHour))
        return
      case "WA":
        dispatch(setSelectedLessonType(lessonTypes.oneHourShared))
        return
      default:
        dispatch(setSelectedLessonType(lessonTypes.standard))
    }
  }

export type SetMeetingPointsAction = ReturnType<typeof setMeetingPoints>
export const setMeetingPoints = (meetingPoints: AddressPoint[]) => ({
  type: SET_MEETING_POINTS,
  payload: meetingPoints,
})

export type SetSpotTakenAction = ReturnType<typeof setSpotTaken>
export const setSpotTaken = (spotTaken: boolean) => ({
  type: SET_SPOT_TAKEN,
  payload: spotTaken,
})

export type SetIsDoneSearchingAction = ReturnType<typeof setIsDoneSearching>
export const setIsDoneSearching = (searching: boolean) => ({
  type: IS_DONE_SEARCHING,
  payload: searching,
})

export type SetIsOutOfServiceAreaAction = ReturnType<
  typeof setIsOutOfServiceArea
>
export const setIsOutOfServiceArea = () => ({ type: OUT_OF_SERVICE_AREA })

export type SetSchedulerFetchErrorAction = ReturnType<
  typeof setSchedulerFetchError
>
export const setSchedulerFetchError = (error: string | null) => ({
  type: SCHEDULE_FETCH_ERROR,
  payload: error,
})

export type SetIsSearchingAction = ReturnType<typeof setIsSearching>
export const setIsSearching = (isSearching: boolean) => ({
  type: IS_SEARCHING,
  payload: isSearching,
})

export type SetSlotsGetSuccessAction = ReturnType<typeof setSlotsGetSuccess>
export const setSlotsGetSuccess = (processedSlots: SlotsData | null) => ({
  type: SLOTS_GET_SUCCESS,
  payload: processedSlots,
})

export type SetSearchDatesAction = ReturnType<typeof setSearchDates>
export const setSearchDates = (dates: {
  startDate: dayjs.Dayjs
  endDate: dayjs.Dayjs
}) => ({
  type: SET_SEARCH_DATES,
  payload: dates,
})

export type SetIncludePrivateAction = ReturnType<typeof setIncludePrivate>
export const setIncludePrivate = (includePrivate: boolean) => ({
  type: SET_INCLUDE_PRIVATE,
  payload: includePrivate,
})

const slotNotTaken = (
  slot: LessonDateTime,
  instructorSlots: LessonDateTime[],
) => {
  // Make sure the `slot` doesn't overlap with anything in `instructorSlots`;
  if (!instructorSlots || instructorSlots.length === 0) {
    return true
  }
  let notTaken = true
  instructorSlots.forEach((instructorSlot) => {
    if (
      slot.startTime < instructorSlot.endTime &&
      slot.endTime > instructorSlot.startTime
    ) {
      notTaken = false
    }
  })
  return notTaken
}

const processAvails = (slots: SchedulerSlot[]) => {
  // We get avails from the server.
  // TEMP June 5, 2020:
  // Then do a sanity check just to make sure none of the avails are overlapping
  // an already-scheduled lesson. This is in response to a bug on the scheduler
  // where the scheduler seems to lose track of our lessons.
  // Last, re-shape our data to be slots by day for the UI to easily consume.

  // Get all of the instructor IDs returned...
  // Get the first date and last date
  let firstSlotStart: number
  let lastSlotEnd: number
  const instructors: string[] = []
  const instructorDict: { [instructorId: string]: Instructor } = {}
  const lessonIds: string[] = []
  slots.forEach((slot) => {
    if (slot.appointment_id) {
      lessonIds.push(slot.appointment_id)
    }

    if (!firstSlotStart || slot.start_time < firstSlotStart) {
      firstSlotStart = slot.start_time
    }
    if (!lastSlotEnd || slot.end_time > lastSlotEnd) {
      lastSlotEnd = slot.end_time
    }
    if (!instructors.includes(slot.provider.id)) {
      instructors.push(slot.provider.id)
    }
  })
  // Then get all the lessons for those instructors in that timespan
  // We do this as a lookup so we only fetch once per instructor, and then
  // do the lookup below.
  const proms: Promise<unknown>[] = []
  const prevInstructorSlots: { [instructorId: string]: LessonDateTime[] } = {}
  instructors.forEach((instructorId) => {
    const dateTimesQuery = query(
      ref(sdk9db, `lessonSlotDateTimes/${instructorId}`),
      orderByChild("startTime"),
      startAt(firstSlotStart),
      endAt(lastSlotEnd),
    )
    proms.push(
      get(dateTimesQuery).then((snap) => {
        snap.forEach((lessonSnap) => {
          const prevLesson = lessonSnap.val()
          if (prevLesson) {
            if (instructorId in prevInstructorSlots) {
              prevInstructorSlots[instructorId].push(prevLesson)
            } else {
              prevInstructorSlots[instructorId] = [prevLesson]
            }
          }
        })
      }),
    )
    proms.push(
      get(ref(sdk9db, `instructors/${instructorId}`)).then((instructorSnap) => {
        const instructor = instructorSnap.val()
        if (instructor) {
          instructorDict[instructorId] = instructor
        }
      }),
    )
  })

  return Promise.all(proms).then(() => {
    // Now prevInstructorSlots will contain a dictionary of instructors and their
    // lessons for the time period in question (and potentially more), so we can verify that
    // each avail returned does not overlap.
    const dateSlots: { [date: string]: SlotToPick[] } = {}
    slots.forEach((slot) => {
      const {
        start_time: startTime,
        end_time: endTime,
        provider: instructor,
        is_private: isPrivate,
        location,
        is_unpaired: isUnpaired,
        appointment_id: lessonId,
      } = slot
      const timeZone =
        instructorDict[instructor.id]?.timeZone || DEFAULT_TIMEZONE
      const slotDate = dayjs.tz(startTime, timeZone).startOf("day").valueOf()
      // Round the times in case we get super-precise values returned
      // from the API.
      const startTimeRound = dayjs
        .tz(startTime, timeZone)
        .startOf("minute")
        .valueOf()
      const endTimeRound = dayjs
        .tz(endTime, timeZone)
        .startOf("minute")
        .valueOf()
      const slotObj = {
        date: slotDate,
        startTime: startTimeRound,
        endTime: endTimeRound,
        instructorId: instructor.id,
        timeZone,
        instructorDisplayName: instructorDict[instructor.id]
          ? instructorDict[instructor.id].displayName
          : "",
        isPrivate,
        location,
        isUnpaired,
        lessonId: lessonId ?? undefined,
      }

      if (
        isUnpaired ||
        slotNotTaken(slotObj, prevInstructorSlots[instructor.id])
      ) {
        if (slotDate in dateSlots) {
          dateSlots[slotDate].push(slotObj)
        } else {
          dateSlots[slotDate] = [slotObj]
        }
      }
    })

    // Sort all the times within the days.
    Object.keys(dateSlots).forEach((slotDate) => {
      dateSlots[slotDate] = dateSlots[slotDate].sort(
        (a, b) => a.startTime - b.startTime,
      )
    })
    return dateSlots
  })
}

const cachedSearch = memoize(
  (queryString: string) =>
    makeApiCall(`${apiConfig.slots}?${queryString}`, {
      method: "GET",
      headers: {
        "api-key": schedulingApiKey,
        "Cache-Control": "no-cache",
        pragma: "no-cache",
      },
    }),
  { maxAge: MAX_MEMOIZE_MS, promise: true },
)

function groupRoadTestTypes(lessonType: LessonTypeName): LessonTypeName {
  // We don't want to send all the different road test types.
  if (
    lessonType.includes("road-test") ||
    lessonType === LessonTypeName.PrepTest60
  ) {
    return LessonTypeName.RoadTest
  }
  return lessonType
}

interface AvailabilityForLocationParams {
  location: AddressPoint
  instructorId: string | undefined
  durationMinutes: number
  startDate: dayjs.Dayjs
  endDate: dayjs.Dayjs
  includePrivate: boolean
  selectedLessonType: LessonType
  lastInstructorId: string | null
  searchState?: string
}

export const getAvailabilityForLocationPayload = ({
  location,
  instructorId,
  durationMinutes,
  startDate,
  endDate,
  includePrivate,
  selectedLessonType,
  lastInstructorId,
  searchState,
}: AvailabilityForLocationParams) => {
  const roundingResolution =
    searchState && searchState.toUpperCase() in STATE_CONFIG_OVERRIDES
      ? STATE_CONFIG_OVERRIDES[searchState.toUpperCase()].rounding_resolution
      : 15
  const onlyService =
    selectedLessonType.stateMeta[searchState?.toLowerCase() || ""]?.onlyService
  return {
    address: location.address.replace("#", ""),
    duration_minutes: durationMinutes,
    start_date: startDate.format(DATE_FORMAT),
    end_date: endDate.format(DATE_FORMAT),
    do_prioritization: true,
    include_private: includePrivate ?? false,
    prefer_afternoons:
      durationMinutes > hoursToMinutes(2) ? false : preferAfternoons,
    maximize_slots_in_avail: durationMinutes < hoursToMinutes(3),
    only_fixed: !!location.isFixedLocation,
    ...(instructorId && { provider_id: instructorId }),
    include_unpaired: selectedLessonType.name === "shared",
    meeting_point_id: location.meetingPointId,
    rounding_resolution: roundingResolution,
    preferred_provider_id: lastInstructorId,
    ...(onlyService && { only_service: onlyService?.join(",") }),
    appointment_type: groupRoadTestTypes(selectedLessonType.name),
  }
}

const getAvailabilityForLocation = (params: AvailabilityForLocationParams) => {
  const { location } = params
  const schedulingPayload = getAvailabilityForLocationPayload(params)

  type PayloadKey = keyof typeof schedulingPayload
  const queryString = Object.keys(schedulingPayload)
    .map((key) => `${key}=${schedulingPayload[key as PayloadKey]}`)
    .join("&")

  return cachedSearch(queryString)
    .then((response: SchedulerReturn) => {
      const { data: slots, warnings } = response

      if (warnings?.includes("Out of service area.")) {
        return "Out of service area"
      }

      // Hydrate the address meta into each slot.
      const hydratedSlots = slots
        ? slots.map((slot) => ({
            ...slot,
            location: {
              ...slot.location,
              distanceMiles: location.distanceMiles,
              meetingPointLabel: location.meetingPointLabel,
              meetingPointId: location.meetingPointId,
            },
          }))
        : []

      return hydratedSlots
    })
    .catch(() => [])
}

const fetchMeetingPoints = async (
  searchGeocode: Coordinates,
  searchState: string,
  selectedLessonType: LessonType | null,
): Promise<GQLAddressPointFragment[]> => {
  try {
    const { data } = await clientNext.query({
      query: GetMeetingPointsDocument,
      variables: {
        query: {
          state: searchState,
          lat: searchGeocode.lat,
          lng: searchGeocode.lng,
          lessonType: selectedLessonType?.name,
        },
      },
    })

    return data.meetingPoints
  } catch (error) {
    logError(error)
    return []
  }
}

const getHomePickupPoint = (searchAddress: string): AddressPoint[] => {
  return [
    {
      address: searchAddress,
      distanceMiles: 0,
      meetingPointLabel: "Home Pickup",
      meetingPointId: "home",
    },
  ]
}

const getMeetingPoints = async (
  searchGeocode: Coordinates,
  searchState: string,
  selectedLessonType: LessonType | null,
  stateConfig: StateConfig,
): Promise<AddressPoint[]> => {
  const points = await fetchMeetingPoints(
    searchGeocode,
    searchState,
    selectedLessonType,
  )
  return points.map((point) => ({
    address: point.address,
    meetingPointLabel: point.label,
    meetingPointId: point.id,
    distanceMiles: point.distanceMiles ?? 0,
    isFixedLocation: stateConfig.hasFixedLocations,
  }))
}

export const searchAddressToPoints = memoize(
  async (
    searchAddress: string,
    searchGeocode: Coordinates | null,
    stateConfig: StateConfig | null,
    searchState: string | null,
    selectedLessonType: LessonType | null,
  ): Promise<AddressPoint[]> => {
    // Given the address, return an array of addresses, which can be just
    // home pickup, just meeting points, or a combination of the two.
    if (!searchState) {
      throw new Error("State required.")
    }

    if (!searchGeocode) {
      throw new Error("Geocode required.")
    }

    if (!stateConfig) {
      throw new Error("State config required.")
    }

    if (
      !stateConfig.hasHomePickup &&
      !stateConfig.hasMeetingPoints &&
      !stateConfig.hasFixedLocations
    ) {
      throw new Error(
        "One of home, meeting point or fixed locations must be enabled.",
      )
    }

    const homePickupPoints = stateConfig.hasHomePickup
      ? getHomePickupPoint(searchAddress)
      : []
    const meetingPoints =
      stateConfig.hasMeetingPoints || stateConfig.hasFixedLocations
        ? await getMeetingPoints(
            searchGeocode,
            searchState,
            selectedLessonType,
            stateConfig,
          )
        : []

    return [...homePickupPoints, ...meetingPoints]
  },
  { promise: true },
)

export const doSearch =
  (
    lessonDurationMinutes: number,
    instructorId: string | undefined,
    addressPoints: AddressPoint[],
  ): AppThunkAction =>
  async (dispatch, getState) => {
    const {
      search: {
        startDate,
        endDate,
        isSearching,
        isStopping,
        outOfServiceArea,
        includePrivate,
        selectedLessonType,
      },
      location: { searchAddress, stateConfig, searchState },
      user: { lessons },
    } = getState()

    const lastInstructorId =
      lessons && lessons.length
        ? lessons[lessons.length - 1].instructor.id
        : null

    if (isStopping) {
      dispatch(setIsSearching(false))
      dispatch(setIsDoneSearching(true))
      return
    }

    if (isSearching || outOfServiceArea) {
      return
    }

    if (!selectedLessonType || !searchAddress) {
      return
    }

    dispatch(setIsSearching(true))
    dispatch(setSchedulerFetchError(null))

    if (!searchState) {
      dispatch(setIsDoneSearching(true))
      dispatch(setIsSearching(false))
      return
    }

    try {
      if (addressPoints.length === 0) {
        dispatch(setIsOutOfServiceArea())
        return
      }
      dispatch(setMeetingPoints(addressPoints))
      const searchResults = await Promise.all(
        addressPoints.map((location) =>
          getAvailabilityForLocation({
            location,
            instructorId,
            durationMinutes: lessonDurationMinutes,
            startDate,
            endDate,
            includePrivate,
            selectedLessonType,
            lastInstructorId,
            searchState: stateConfig?.shortName,
          }),
        ),
      )

      /**
       * First thing we do here is check for out of service area and
       * flatten down all the different results into one big array
       * so we can more easily deal with it downstream.
       */
      if (!searchResults) return
      if (searchResults.every((res) => res === "Out of service area")) {
        dispatch(setIsOutOfServiceArea())
        return
      }
      // Remove the out of service area results. We could get here if there
      // are some out of service area and some not.
      const onlySlotResults = searchResults.filter(
        (slot) => !!slot && slot !== "Out of service area",
      )

      // And flatten.
      const slots = onlySlotResults.reduce<SchedulerSlot[]>(
        (acc, val) => acc.concat(val as SchedulerSlot[]),
        [],
      )

      if (slots) {
        processAvails(slots).then((processedSlots) => {
          dispatch(setSlotsGetSuccess(processedSlots))
          // Now check if we need to search more.
          const numDays = processedSlots
            ? Object.keys(processedSlots).length
            : 0
          if (
            numDays >= MIN_RESULT_DAYS ||
            endDate >= dayjs().add(MAX_DAYS_OUT, "days")
          ) {
            // We're done.
            dispatch(setIsDoneSearching(true))
            dispatch(setIsSearching(false))
          } else {
            // We don't have enough results, so search again.
            const newEndDate = endDate.clone().add(DAYS_AT_A_TIME, "days")
            const newStartDate = startDate.clone().add(DAYS_AT_A_TIME, "days")
            // Dispatch this either way so upstream listeners know what to do, too.
            dispatch(
              setSearchDates({
                startDate: newStartDate,
                endDate: newEndDate,
              }),
            )
            if (
              newEndDate <= dayjs().add(MAX_DAYS_OUT, "days") &&
              !isStopping
            ) {
              dispatch(setIsSearching(false))
              dispatch(
                doSearch(lessonDurationMinutes, instructorId, addressPoints),
              )
            } else {
              dispatch(setIsDoneSearching(true))
              dispatch(setIsSearching(false))
            }
          }
        })
      } else {
        dispatch(setSlotsGetSuccess(null))
      }
    } catch (error: any) {
      logError(error)
      dispatch(setSchedulerFetchError(error.message))
      dispatch(setIsDoneSearching(true))
      dispatch(setIsSearching(false))
    }
  }

export const startNewSearch =
  (
    lessonDurationMinutes: number,
    instructorId: string | undefined,
    addressPoints: AddressPoint[],
  ): AppThunkAction =>
  async (dispatch, getState) => {
    // This is a helper function to call doSearch but ensuring it's finished the
    // previous search (if applicable). It should be the entry to any new search.
    // Dispatch the stop call and then check if we're still searching. If we are,
    // we need to wait... we can safely keep dispatching since once we are no longer
    // searching we'll reset the search state to start fresh.
    const { isSearching } = getState().search
    const { searchCounty, searchGeocode, stateConfig } = getState().location

    if (!isSearching && searchCounty && searchGeocode && stateConfig) {
      dispatch(doSearch(lessonDurationMinutes, instructorId, addressPoints))
      return
    }
    // Recurse.
    setTimeout(
      () =>
        dispatch(
          startNewSearch(lessonDurationMinutes, instructorId, addressPoints),
        ),
      200,
    )
  }

export const doMoreSearch =
  (
    lessonDurationMinutes: number,
    instructorId: string | undefined,
    addressPoints: AddressPoint[],
  ): AppThunkAction =>
  (dispatch, getState) => {
    // This is called from the "Load More" button on the date list.
    const { startDate, endDate } = getState().search

    const newEndDate = endDate.add(DAYS_AT_A_TIME * 2, "days")
    // This only adds a couple days, so it will get slower as more is clicked more.
    const newStartDate = startDate.add(DAYS_AT_A_TIME, "days")
    dispatch(
      setSearchDates({
        startDate: newStartDate,
        endDate: newEndDate,
      }),
    )
    dispatch(doSearch(lessonDurationMinutes, instructorId, addressPoints))
  }

export const meetingPointsToDescription = (
  points: AddressPoint[],
  includeMiles: boolean,
): string => {
  const pointsWithoutHome = points.filter(
    (point) => point.meetingPointId !== "home",
  )
  if (pointsWithoutHome.length === 0) {
    logErrorMessage(
      "No meeting points were found where we expected to find some",
      { points },
    )
    return "near you."
  }
  const formattedPoints = pointsWithoutHome.map((point) =>
    includeMiles
      ? `${point.meetingPointLabel} ${Math.floor(point.distanceMiles)} miles away`
      : point.meetingPointLabel,
  )
  return `${formattedPoints.slice(0, -1).join(", ")}, or ${formattedPoints[formattedPoints.length - 1]}.`
}
